import React, {
  useEffect, useRef, useState, useCallback,
} from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { FixedSizeList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { ListItem, ListItemText, Skeleton } from '@mui/material';
import DeviceRow from './DeviceRow';
import { PAGE_SIZE } from '../common/util/deviceData';

const useStyles = makeStyles((theme) => ({
  list: {
    maxHeight: '100%',
  },
  listInner: {
    position: 'relative',
    margin: theme.spacing(1.5, 0),
  },
}));

// A row whose page has not landed yet.
const PlaceholderRow = ({ style }) => (
  <div style={style}>
    <ListItem>
      <ListItemText
        primary={<Skeleton width="60%" />}
        secondary={<Skeleton width="40%" />}
      />
    </ListItem>
  </div>
);

// The list is `total` rows long from the start, so any row can be scrolled
// to, and pages are asked for as their rows come on screen.
const DeviceList = ({
  rows, total, loadPage, onVisibleChange, scrollTo,
}) => {
  const classes = useStyles();
  const listInnerEl = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (listInnerEl.current) {
      listInnerEl.current.className = classes.listInner;
    }
  }, [listInnerEl, classes.listInner]);

  const [, setTime] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => setTime(Date.now()), 60000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  // Bring the selected row into view once its index is known. Scrolling to
  // it also asks for its page, through onItemsRendered.
  useEffect(() => {
    if (scrollTo && listRef.current && total && scrollTo.index < total) {
      listRef.current.scrollToItem(scrollTo.index, 'smart');
    }
  }, [scrollTo, total]);

  const rowRenderer = useCallback(({ index, style, data }) => {
    if (!data[index]) {
      return <PlaceholderRow style={style} />;
    }
    return <DeviceRow data={data} index={index} style={style} />;
  }, []);

  // Ask for the pages under the drawn rows, and tell the page which devices
  // are drawn, including the overscan, so the socket subscription covers
  // what a small scroll would reveal.
  const onItemsRendered = useCallback(({ overscanStartIndex, overscanStopIndex }) => {
    const firstPage = Math.floor(overscanStartIndex / PAGE_SIZE);
    const lastPage = Math.floor(overscanStopIndex / PAGE_SIZE);
    for (let page = firstPage; page <= lastPage; page += 1) {
      loadPage(page);
    }
    if (onVisibleChange) {
      onVisibleChange(rows
        .slice(overscanStartIndex, overscanStopIndex + 1)
        .filter(Boolean)
        .map((device) => device.id));
    }
  }, [rows, loadPage, onVisibleChange]);

  // An empty list has nothing rendered to report, but the subscription
  // should still drop the rows of the previous query.
  useEffect(() => {
    if (onVisibleChange && !rows.length) {
      onVisibleChange([]);
    }
  }, [rows.length, onVisibleChange]);

  return (
    <AutoSizer className={classes.list}>
      {({ height, width }) => (
        <FixedSizeList
          ref={listRef}
          width={width}
          height={height}
          itemCount={total || 0}
          itemData={rows}
          itemSize={72}
          overscanCount={10}
          innerRef={listInnerEl}
          onItemsRendered={onItemsRendered}
        >
          {rowRenderer}
        </FixedSizeList>
      )}
    </AutoSizer>
  );
};

export default React.memo(DeviceList);
